import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { mediaSrc } from "@/lib/media";
import { Lock, PlayCircle, CheckCircle2 } from "lucide-react";

export const ProgressBar = ({ percent }) => (
  <div className="h-1.5 w-full rounded-full bg-slate-200 dark:bg-white/10 overflow-hidden">
    <div
      className="h-full rounded-full bg-gradient-to-r from-[#2563EB] to-[#06B6D4] transition-all duration-500"
      style={{ width: `${percent || 0}%` }}
    />
  </div>
);

export const CourseCard = ({ course, index = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 14 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, delay: Math.min(index * 0.05, 0.3) }}
    data-testid={`student-course-card-${course.slug}`}
    className="group rounded-2xl border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/[0.03] overflow-hidden hover:shadow-lg hover:shadow-slate-900/5 transition-shadow"
  >
    <div className="relative h-32 sm:h-36 overflow-hidden bg-slate-100 dark:bg-white/5">
      {course.cover ? (
        <img
          src={mediaSrc(course.cover)}
          alt={course.title}
          loading="lazy"
          className="h-full w-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
        />
      ) : null}
      {course.status === "completed" && (
        <span className="absolute top-3 left-3 inline-flex items-center gap-1 rounded-full bg-emerald-500 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white">
          <CheckCircle2 className="h-3 w-3" /> Completed
        </span>
      )}
    </div>
    <div className="p-4">
      <h3 className="font-display text-base font-semibold tracking-tight line-clamp-1">{course.title}</h3>
      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400 line-clamp-2 leading-relaxed">
        {course.short}
      </p>

      <div className="mt-3.5 flex items-center justify-between text-[11px] text-slate-500 dark:text-slate-400">
        <span data-testid={`course-progress-label-${course.slug}`}>
          {course.completed_sessions}/{course.total_sessions} sessions
        </span>
        <span className="font-semibold text-[#0F172A] dark:text-white">{course.percent}%</span>
      </div>
      <div className="mt-2">
        <ProgressBar percent={course.percent} />
      </div>

      <Link
        to={`/student/learn/${course.slug}`}
        data-testid={`continue-learning-${course.slug}`}
        className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#0F172A] dark:bg-white text-white dark:text-[#0F172A] px-4 py-2.5 text-xs font-semibold hover:opacity-90 transition-opacity"
      >
        <PlayCircle className="h-3.5 w-3.5" />
        {course.percent > 0 ? "Continue Learning" : "Start Learning"}
      </Link>
      {course.last_accessed_at && (
        <div className="mt-2 text-center text-[10px] text-slate-400">
          Last accessed {new Date(course.last_accessed_at).toLocaleDateString()}
        </div>
      )}
    </div>
  </motion.div>
);

export const LockedCourseCard = ({ course }) => (
  <div
    data-testid={`locked-course-card-${course.slug}`}
    className="rounded-2xl border border-dashed border-slate-200 dark:border-white/10 bg-slate-50/60 dark:bg-white/[0.015] overflow-hidden"
  >
    <div className="relative h-24 overflow-hidden bg-slate-100 dark:bg-white/5">
      {course.cover ? (
        <img src={mediaSrc(course.cover)} alt="" loading="lazy" className="h-full w-full object-cover opacity-35" />
      ) : null}
      <span className="absolute top-2.5 left-2.5 inline-flex items-center gap-1 rounded-full bg-slate-900/70 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white">
        <Lock className="h-2.5 w-2.5" /> Locked
      </span>
    </div>
    <div className="p-3.5">
      <h3 className="text-sm font-medium text-slate-600 dark:text-slate-300 line-clamp-1">{course.title}</h3>
      <p className="mt-1 text-[11px] text-slate-400">Unlock with {course.unlock_with}</p>
      <Link
        to="/#packages"
        data-testid={`upgrade-cta-${course.slug}`}
        className="mt-3 w-full inline-flex items-center justify-center rounded-full border border-slate-300 dark:border-white/15 px-3 py-2 text-[11px] font-medium text-slate-600 dark:text-slate-300 hover:bg-white dark:hover:bg-white/5"
      >
        View all packages
      </Link>
    </div>
  </div>
);
